import React, { useState, useRef, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  queryAllDocsByField,
  addNewDoc,
  storage,
  db,
  queryAllDocsByFieldSnap,
} from "../../utils/firebase/Firebase.utils";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import toast from "react-hot-toast";
import Webcam from "react-webcam";
import { collection, query, where, getDocs } from "firebase/firestore";
import { sendBulkNotifications } from "../../utils/notifications";
import { dualNotification } from "../../utils/EleghoUtils";
import MobileWrapper from "../../components/MobileWrapper";
import { motion } from "framer-motion";
import { v4 } from "uuid";
import {
  FiUser,
  FiHome,
  FiCalendar,
  FiClock,
  FiMapPin,
  FiUpload,
  FiCamera,
  FiPackage,
  FiX,
  FiCheck,
  FiRefreshCw,
  FiList,
  FiTruck,
  FiArchive,
} from "react-icons/fi";
import { adminEmailAddress, unitNumberLabel } from "../../utils/constants";

export default function Parcels() {
  const currentUser = useSelector((state) => state.userObject);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    unitNumber: "",
    recipientName: "",
    parcelNumber: "",
    dateTime: "",
    deliveryCompany: "",
    storageLocation: "parcel room",
    image: null,
  });
  const [showCamera, setShowCamera] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [facingMode, setFacingMode] = useState("environment");
  const [cameraError, setCameraError] = useState("");
  const webcamRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [residents, setResidents] = useState([]);
  const [filteredResidents, setFilteredResidents] = useState([]);
  const [parcelId, setParcelId] = useState(v4().slice(0, 8).toUpperCase());

  // Color scheme
  const eleghoBlue = "#363062";
  const eleghoGreen = "#8FCD00";

  // Set current date and time as default
  useEffect(() => {
    const now = new Date();
    const localDateTime = new Date(now - now.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 16);

    setFormData((prevData) => ({
      ...prevData,
      dateTime: localDateTime,
    }));
  }, []);

  // Fetch residents data for autocomplete
  useEffect(() => {
    const fetchResidents = async () => {
      try {
        const residentsData = await queryAllDocsByField(
          "residents",
          "status",
          "==",
          "occupied"
        );
        setResidents(residentsData || []);
      } catch (error) {
        console.error("Error fetching residents:", error);
        toast.error("Failed to load resident data");
      }
    };

    fetchResidents();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    // If changing unit number, handle search
    if (name === "unitNumber") {
      setSearchTerm(value);

      if (value.trim() !== "") {
        const filtered = residents.filter(
          (resident) =>
            resident.identifier?.toLowerCase().includes(value.toLowerCase()) ||
            resident.lot?.toLowerCase().includes(value.toLowerCase())
        );
        setFilteredResidents(filtered);
      } else {
        setFilteredResidents([]);
      }
    }
  };

  const handleResidentSelect = (resident) => {
    setFormData({
      ...formData,
      unitNumber:
        resident.identifier || `Unit ${resident.lot} - Tower ${resident.phase}`,
    });
    setFilteredResidents([]);
    setSearchTerm("");
  };

  const startCamera = () => {
    setCameraError("");
    setShowCamera(true);
  };

  const stopCamera = () => {
    setShowCamera(false);
  };

  const switchCamera = () => {
    setFacingMode((prevMode) =>
      prevMode === "environment" ? "user" : "environment"
    );
  };

  const capturePhoto = useCallback(() => {
    if (!webcamRef.current) {
      toast.error("Camera not initialized properly");
      return;
    }

    try {
      const imageSrc = webcamRef.current.getScreenshot();

      if (!imageSrc) {
        toast.error("Failed to capture image");
        return;
      }

      // Convert base64 to blob
      fetch(imageSrc)
        .then((res) => res.blob())
        .then((blob) => {
          setFormData((prev) => ({
            ...prev,
            image: blob,
          }));
          stopCamera();
        })
        .catch((error) => {
          console.error("Error converting image:", error);
          toast.error("Failed to process captured image");
        });
    } catch (error) {
      console.error("Error capturing photo:", error);
      toast.error("Failed to capture photo");
    }
  }, [webcamRef]);

  const handleCameraError = useCallback((error) => {
    console.error("Camera error:", error);
    setCameraError(
      "Failed to access camera. Please check permissions and try again."
    );
    toast.error("Camera access error");
  }, []);

  const handleImageCapture = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFormData({
        ...formData,
        image: e.target.files[0],
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.unitNumber) {
      toast.error(`Please enter a ${unitNumberLabel}`);
      return;
    }

    if (!formData.image) {
      toast.error("Please capture a photo of the parcel");
      return;
    }

    try {
      setIsSubmitting(true);
      setUploadProgress(0);

      // Create parcel data object
      const parcelData = {
        unitNumber: formData.unitNumber,
        recipientName: formData.recipientName || "Not specified",
        parcelNumber: formData.parcelNumber || "Not specified",
        dateTime: formData.dateTime,
        deliveryCompany: formData.deliveryCompany || "Not specified",
        storageLocation: formData.storageLocation,
        status: "Stored",
        createdAt: new Date().toISOString(),
        createdBy: currentUser?.email || "Unknown",
        createdByName: currentUser?.displayName || "Unknown",
      };

      // If image is a blob (from camera), we need to create a file
      let imageFile = formData.image;
      if (formData.image instanceof Blob && !(formData.image instanceof File)) {
        imageFile = new File([formData.image], `parcel_${Date.now()}.jpg`, {
          type: "image/jpeg",
        });
      }

      // Upload the image to Firebase Storage
      const storageRef = ref(
        storage,
        `parcels/${Date.now()}_${imageFile.name.replace(/[^a-zA-Z0-9.]/g, "_")}`
      );

      const uploadTask = uploadBytesResumable(storageRef, imageFile);

      uploadTask.on(
        "state_changed",
        (snapshot) => {
          // Track upload progress
          const progress = Math.round(
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100
          );
          setUploadProgress(progress);
        },
        (error) => {
          setIsSubmitting(false);
          console.error("Upload error:", error);

          // Handle specific error types
          if (error.code === "storage/unauthorized") {
            toast.error(
              "Permission denied. You don't have access to upload files."
            );
          } else if (error.code === "storage/canceled") {
            toast.error("Upload was canceled");
          } else if (error.code === "storage/unknown") {
            toast.error("Unknown error occurred during upload");
          } else {
            toast.error(`Failed to upload image: ${error.message}`);
          }
        },
        async () => {
          try {
            // Get the download URL after upload completes
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);

            // Add the image URL to the parcel data
            parcelData.imageUrl = downloadURL;

            // Save to Firestore
            const docRef = await addNewDoc("parcels", parcelData);

            if (docRef) {
              // Find users with matching linkedIdentifiers for the unit number
              const usersRef = collection(db, "users");
              const q = query(
                usersRef,
                where(
                  "linkedIdentifiers",
                  "array-contains",
                  formData.unitNumber
                )
              );
              const querySnapshot = await getDocs(q);

              if (!querySnapshot.empty) {
                // Collect user emails for notification
                const users = [];
                querySnapshot.forEach((doc) => {
                  const userData = doc.data();
                  if (userData) {
                    users.push(userData);
                  }
                });

                // Send notifications to all users associated with the unit
                if (users.length > 0) {
                  await Promise.all(
                    users.map(async (user) => {
                      const notification = {
                        recipient: user.email,
                        recipientName: user.displayName,
                        sender: currentUser?.email,
                        senderName: currentUser?.displayName,
                        subject: "New Parcel Received",
                        message: `A parcel has been delivered for ${formData.recipientName} and is stored at ${formData.storageLocation}`,
                        type: "parcel",
                        url: `/parcels/${docRef.id}`,
                        id: docRef.id,
                        read: false,
                      };

                      await dualNotification(notification);
                    })
                  );
                } else {
                }
              } else {
              }

              const adminNotification = {
                recipient: adminEmailAddress,
                recipientName: "Admin",
                sender: currentUser?.email,
                senderName: currentUser?.displayName,
                subject: "New Parcel Received",
                message: `A parcel has been delivered for unit ${formData.unitNumber} and is stored at ${formData.storageLocation}`,
                type: "parcel",
                url: `/parcels/${docRef.id}`,
                id: docRef.id,
                read: false,
              };

              await dualNotification(adminNotification);

              toast.success("Parcel logged successfully");
              // Reset form
              setFormData({
                unitNumber: "",
                recipientName: "",
                parcelNumber: "",
                dateTime: "",
                deliveryCompany: "",
                storageLocation: "",
                image: null,
              });
              // Redirect to parcel logs page
              navigate("/parcel-logs");
            } else {
              throw new Error("Failed to create document");
            }
          } catch (firestoreError) {
            setIsSubmitting(false);
            console.error("Firestore error:", firestoreError);
            toast.error(
              `Failed to save parcel data: ${firestoreError.message}`
            );
          }
        }
      );
    } catch (error) {
      setIsSubmitting(false);
      console.error("Parcel logging error:", error);
      toast.error(`An error occurred: ${error.message}`);
    }
  };

  const videoConstraints = {
    facingMode: facingMode,
    width: { ideal: 1280 },
    height: { ideal: 720 },
  };

  return (
    <MobileWrapper label="Parcel Logging">
      <div className="min-h-screen bg-gray-50 p-4 pb-6">
        {/* Header similar to GuestForm.jsx */}
        <div className="flex items-center justify-between mb-5 bg-[#363062]/5 p-3 rounded-lg">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-[#363062] rounded-lg">
              <FiPackage className="text-white text-sm" />
            </div>
            <div>
              <h1 className="text-xs font-medium text-[#363062]">
                Parcel Logging
              </h1>
              <p className="text-xs text-gray-500">
                Register parcels for resident collection
              </p>
              <p className="text-xs text-gray-500 mt-0.5">Ref: {parcelId}</p>
            </div>
          </div>
          <Link
            to="/parcel-logs"
            className="text-xs text-eleghoBlue flex items-center"
          >
            <span>View Logs</span>
          </Link>
        </div>

        {showCamera ? (
          <div className="fixed inset-0 z-50 bg-black flex flex-col">
            <div className="relative flex-1 w-full">
              {cameraError ? (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="bg-red-500 text-white p-4 rounded-lg max-w-xs text-center">
                    <p className="mb-2 font-semibold">Camera Error</p>
                    <p>{cameraError}</p>
                    <button
                      onClick={stopCamera}
                      className="mt-3 bg-white text-red-500 px-4 py-2 rounded-lg font-medium"
                    >
                      Close Camera
                    </button>
                  </div>
                </div>
              ) : (
                <Webcam
                  audio={false}
                  ref={webcamRef}
                  screenshotFormat="image/jpeg"
                  videoConstraints={videoConstraints}
                  onUserMediaError={handleCameraError}
                  className="w-full h-full object-cover"
                />
              )}
              <div className="absolute bottom-6 inset-x-0 flex justify-center space-x-6">
                <button
                  type="button"
                  onClick={stopCamera}
                  className="p-3 bg-white bg-opacity-20 rounded-full shadow-lg"
                >
                  <FiX className="h-8 w-8 text-white" />
                </button>
                <button
                  type="button"
                  onClick={capturePhoto}
                  className="p-4 bg-white rounded-full shadow-lg"
                >
                  <div className="h-12 w-12 rounded-full border-4 border-eleghoGreen"></div>
                </button>
                <button
                  type="button"
                  onClick={switchCamera}
                  className="p-3 bg-white bg-opacity-20 rounded-full shadow-lg"
                >
                  <FiRefreshCw className="h-8 w-8 text-white" />
                </button>
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Unit Information */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-lg shadow-sm p-4"
            >
              <h2
                className="text-xs font-semibold mb-3 flex items-center gap-2"
                style={{ color: eleghoBlue }}
              >
                <FiHome className="w-3.5 h-3.5" />
                Unit Information
              </h2>

              <div className="space-y-3">
                <div className="relative">
                  <label className="flex items-center gap-2 text-xs font-medium text-gray-700 mb-1">
                    {unitNumberLabel} *
                  </label>
                  <input
                    type="text"
                    name="unitNumber"
                    value={formData.unitNumber}
                    onChange={handleChange}
                    placeholder={`Enter ${unitNumberLabel}`}
                    required
                    className="w-full px-3 py-2 border border-gray-200 rounded-md focus:ring-1 focus:ring-eleghoBlue/20 focus:border-eleghoBlue text-xs"
                  />
                  {filteredResidents.length > 0 && (
                    <ul className="absolute z-10 w-full bg-white mt-1 border border-gray-200 rounded-md shadow-lg max-h-48 overflow-auto">
                      {filteredResidents.map((resident) => (
                        <li
                          key={resident.id}
                          onClick={() => handleResidentSelect(resident)}
                          className="px-3 py-2 hover:bg-gray-50 cursor-pointer text-xs"
                        >
                          {resident.identifier ||
                            `Unit ${resident.lot} - Tower ${resident.phase}`}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </motion.div>

            {/* Parcel Details */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-lg shadow-sm p-4"
            >
              <h2
                className="text-xs font-semibold mb-3 flex items-center gap-2"
                style={{ color: eleghoBlue }}
              >
                <FiPackage className="w-3.5 h-3.5" />
                Parcel Details
              </h2>

              <div className="space-y-3">
                <div>
                  <label className="flex items-center gap-2 text-xs font-medium text-gray-700 mb-1">
                    Recipient Name
                  </label>
                  <input
                    type="text"
                    name="recipientName"
                    value={formData.recipientName}
                    onChange={handleChange}
                    placeholder="Enter name on the parcel"
                    className="w-full px-3 py-2 border border-gray-200 rounded-md focus:ring-1 focus:ring-eleghoBlue/20 focus:border-eleghoBlue text-xs"
                  />
                </div>

                <div>
                  <label className="flex items-center gap-2 text-xs font-medium text-gray-700 mb-1">
                    Parcel Number
                  </label>
                  <input
                    type="text"
                    name="parcelNumber"
                    value={formData.parcelNumber}
                    onChange={handleChange}
                    placeholder="Enter parcel tag number"
                    className="w-full px-3 py-2 border border-gray-200 rounded-md focus:ring-1 focus:ring-eleghoBlue/20 focus:border-eleghoBlue text-xs"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="flex items-center gap-2 text-xs font-medium text-gray-700 mb-1">
                      <FiCalendar className="w-3.5 h-3.5 text-gray-500" />
                      Date *
                    </label>
                    <input
                      type="datetime-local"
                      name="dateTime"
                      value={formData.dateTime}
                      onChange={handleChange}
                      required
                      className="w-full px-3 py-2 border border-gray-200 rounded-md focus:ring-1 focus:ring-eleghoBlue/20 focus:border-eleghoBlue text-xs"
                    />
                  </div>
                  <div>
                    <label className="flex items-center gap-2 text-xs font-medium text-gray-700 mb-1">
                      <FiTruck className="w-3.5 h-3.5 text-gray-500" />
                      Delivery Company
                    </label>
                    <input
                      type="text"
                      name="deliveryCompany"
                      value={formData.deliveryCompany}
                      onChange={handleChange}
                      placeholder="Enter company name"
                      className="w-full px-3 py-2 border border-gray-200 rounded-md focus:ring-1 focus:ring-eleghoBlue/20 focus:border-eleghoBlue text-xs"
                    />
                  </div>
                </div>

                <div>
                  <label className="flex items-center gap-2 text-xs font-medium text-gray-700 mb-1">
                    <FiArchive className="w-3.5 h-3.5 text-gray-500" />
                    Storage Location *
                  </label>
                  <input
                    type="text"
                    name="storageLocation"
                    value={formData.storageLocation}
                    onChange={handleChange}
                    placeholder="Where the parcel is stored"
                    required
                    className="w-full px-3 py-2 border border-gray-200 rounded-md focus:ring-1 focus:ring-eleghoBlue/20 focus:border-eleghoBlue text-xs"
                  />
                </div>
              </div>
            </motion.div>

            {/* Parcel Photo */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-lg shadow-sm p-4"
            >
              <h2
                className="text-xs font-semibold mb-3 flex items-center gap-2"
                style={{ color: eleghoBlue }}
              >
                <FiCamera className="w-3.5 h-3.5" />
                Parcel Photo
              </h2>

              <div className="space-y-3">
                {formData.image ? (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="w-full relative"
                  >
                    <img
                      src={
                        formData.image instanceof Blob
                          ? URL.createObjectURL(formData.image)
                          : formData.image
                      }
                      alt="Parcel"
                      className="h-56 w-full object-cover rounded-md shadow-sm"
                    />
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, image: null })}
                      className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full shadow-md"
                    >
                      <FiX className="h-4 w-4" />
                    </button>
                  </motion.div>
                ) : (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="w-full h-56 border-2 border-dashed border-gray-200 rounded-md flex items-center justify-center bg-gray-50"
                    onClick={startCamera}
                  >
                    <div className="text-center p-4">
                      <FiCamera className="h-12 w-12 mx-auto text-eleghoBlue opacity-50" />
                      <p className="mt-2 text-xs text-gray-500">
                        Tap to capture a photo of the parcel
                      </p>
                    </div>
                  </motion.div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <motion.button
                    type="button"
                    onClick={startCamera}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="py-2 px-4 bg-eleghoBlue text-white rounded-md text-xs flex items-center justify-center gap-2"
                  >
                    <FiCamera className="w-3.5 h-3.5" />
                    {formData.image ? "Retake Photo" : "Take Photo"}
                  </motion.button>

                  <div>
                    <label
                      htmlFor="parcelPhoto"
                      className="w-full py-2 px-4 bg-gray-200 text-gray-700 rounded-md flex items-center justify-center gap-2 text-xs cursor-pointer"
                    >
                      <FiUpload className="w-3.5 h-3.5" />
                      Upload from Gallery
                    </label>
                    <input
                      id="parcelPhoto"
                      type="file"
                      accept="image/*"
                      onChange={handleImageCapture}
                      className="hidden"
                    />
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Submit Button */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="space-y-3 pt-2"
            >
              <motion.button
                type="submit"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                disabled={isSubmitting}
                className="w-full py-2.5 rounded-md text-white text-xs font-medium flex items-center justify-center gap-2"
                style={{ backgroundColor: eleghoBlue }}
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {uploadProgress > 0
                      ? `Uploading... ${uploadProgress}%`
                      : "Logging Parcel..."}
                  </>
                ) : (
                  <>
                    <FiCheck className="w-3.5 h-3.5" />
                    Log Parcel
                  </>
                )}
              </motion.button>

              <motion.button
                type="button"
                onClick={() => navigate("/parcel-logs")}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full py-2.5 border rounded-md text-xs"
                style={{ borderColor: "#ddd", color: "#666" }}
                disabled={isSubmitting}
              >
                Cancel
              </motion.button>
            </motion.div>

            <p className="text-[10px] text-center text-gray-500 pt-1">
              Notice: All parcels must be photographed for verification and
              tracking purposes. Residents will be notified automatically when
              parcels are registered.
            </p>
          </form>
        )}
      </div>
    </MobileWrapper>
  );
}
